import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import { Label } from '~/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';

export interface FormSelectOption {
  label: string;
  value: string;
}

export interface FormSelectProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> {
  /** react-hook-form control object from `useForm`. */
  control: Control<TFieldValues>;
  /** Registered field name — must match a key in your form schema. */
  name: TName;
  /** Label rendered above the select. Omit to render without a label. */
  label?: string;
  /** Placeholder shown when no value is selected. */
  placeholder?: string;
  /** Selectable options. */
  options: FormSelectOption[];
  /** Disables the select. */
  disabled?: boolean;
  /** Optional side-effect fired with the new value alongside the form update (e.g. live-persist a UI-only preference). */
  onValueChange?: (value: string) => void;
  /** `data-testid` on the trigger (default `${name}-select`); options derive `${testId}-option-${value}`. */
  testId?: string;
}

/**
 * A controlled select field for use with react-hook-form.
 *
 * Wraps shadcn's `Select` with a `Controller` so validation errors and
 * form state are managed automatically.
 *
 * @example
 * ```tsx
 * const { control } = useForm<{ enrichmentType: string }>();
 *
 * <FormSelect
 *   control={control}
 *   name="enrichmentType"
 *   label="Enrichment Type"
 *   placeholder="All"
 *   options={[
 *     { label: 'All', value: 'all' },
 *     { label: 'Full', value: 'full' },
 *     { label: 'Standard', value: 'standard' },
 *   ]}
 * />
 * ```
 */
export function FormSelect<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  placeholder = 'Select…',
  options,
  disabled,
  onValueChange,
  testId,
}: FormSelectProps<TFieldValues, TName>) {
  const resolvedTestId = testId ?? `${name}-select`;
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <div className="space-y-1.5">
          {label !== undefined && (
            <Label htmlFor={name} className="text-sm font-medium">
              {label}
            </Label>
          )}
          <Select
            {...(field.value !== undefined && { value: field.value as string })}
            onValueChange={(value) => {
              field.onChange(value);
              onValueChange?.(value);
            }}
            {...(disabled !== undefined && { disabled })}
          >
            <SelectTrigger id={name} onBlur={field.onBlur} data-testid={resolvedTestId}>
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent data-testid={`${resolvedTestId}-content`}>
              {options.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  data-testid={`${resolvedTestId}-option-${opt.value}`}
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {fieldState.error?.message !== undefined && (
            <p
              role="alert"
              className="text-destructive text-xs"
              data-testid={`${resolvedTestId}-error`}
            >
              {fieldState.error.message}
            </p>
          )}
        </div>
      )}
    />
  );
}
